import api from './api';

export const communicationError = (error: unknown, fallback = 'Could not complete this request.'): string => {
  const err = error as { responseData?: Record<string, unknown>; response?: { data?: Record<string, unknown> }; message?: string };
  const data = err?.responseData ?? err?.response?.data;
  if (data) return Object.entries(data).map(([key, value]) =>
    `${['error', 'detail', 'non_field_errors'].includes(key) ? '' : key.replaceAll('_', ' ') + ': '}${Array.isArray(value) ? value.join(' ') : typeof value === 'object' ? JSON.stringify(value) : String(value)}`).join(' ');
  return err?.message || fallback;
};

/** Keep the same request key after a network interruption, including across reloads.
 * Storage contains only a digest and random UUID, never message content or phone numbers.
 */
export async function postCommunication<T>(path: string, payload: object, identity: object = payload) {
  const me = (await api.getCurrentUser()).data;
  const scope = [me.id, typeof me.organization === 'object' ? me.organization?.id : me.organization, path, identity];
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(scope)));
  const storageKey = 'apexos:communication:' + Array.from(new Uint8Array(bytes)).map(n => n.toString(16).padStart(2, '0')).join('');
  let operationKey = sessionStorage.getItem(storageKey);
  if (!operationKey) {
    operationKey = crypto.randomUUID();
    sessionStorage.setItem(storageKey, operationKey);
  }
  try {
    const response = await api.post<T>(path, { ...payload, operation_key: operationKey });
    const status = (response.data as { status?: string })?.status;
    if (!['unknown', 'dispatching', 'outcome_unknown'].includes(status || '')) sessionStorage.removeItem(storageKey);
    return response;
  } catch (error) {
    const data = (error as { responseData?: { status?: string } })?.responseData;
    if (data?.status === 'failed') sessionStorage.removeItem(storageKey);
    throw error;
  }
}

export const deliveryLabel = (status: string) => ({
  queued: 'Queued', dispatching: 'Submitting', sending: 'Sending', accepted: 'Accepted',
  sent: 'Sent', delivered: 'Delivered', read: 'Read', partial: 'Partial delivery',
  outcome_unknown: 'Outcome unknown — do not resend', unknown: 'Needs reconciliation — do not repeat',
  failed: 'Failed', undelivered: 'Undelivered', blocked: 'Blocked', completed: 'Completed',
  pending_registration: 'Awaiting carrier activation', registered: 'Carrier registered',
  unregistered: 'Not registered', failure: 'Carrier activation failed',
  pending_deregistration: 'Deregistration pending',
}[status] || status.replaceAll('_', ' '));
